import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import { Toast } from '../../components/toast/toast';
import { economicActivities } from '../../mocks/EconomicActivities';
import { ivaStatuses } from '../../mocks/IvaStatuses';
import { clientData, ReferrerName } from '../../types/interfaces';
import { useReferrerNames } from '../../hooks/useReferrerNames';
import { useOfficers } from '../../hooks/useOfficers';
import { useClientTheme } from '../../context/ThemeContext';

interface BasicDataProps {
  clientInfo: clientData;
}

const BasicData: React.FC<BasicDataProps> = ({ clientInfo }) => {
  const { referrerNames } = useReferrerNames();
  const { officers } = useOfficers();
  const { clientTheme } = useClientTheme();
  
  // Obtener configuración
  const config = JSON.parse(localStorage.getItem('config') ?? '{}');
  const isClientCategoryTypeEnabled = config?.isClientCategoryTypeEnabled ?? false;
  const isApplicationEffectiveDateEnabled = config?.isApplicationEffectiveDateEnabled ?? false;
  const isApplicationCreditTypeEnabled = config?.isApplicationCreditTypeEnabled ?? false;
  
  // Helper para obtener el label del referrer name
  const getReferrerNameLabel = (referrerName: string | ReferrerName | undefined): string => {
    if (!referrerName) return '—';
    if (typeof referrerName === 'string') {
      const found = referrerNames.find(rn => rn.code === referrerName);
      return found ? found.name : referrerName;
    }
    return referrerName.name;
  };

  return (
    <Card width='85vw'>
      <Toast id='application-toast' />

      <Card
        variant={clientTheme ? 'primary' : 'success'}
        modifier='weak'
        spacing={'2'}
        display='flex'
        justifyContent='space-between'
      >
        <H
          type='3'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Datos Generales
        </H>
      </Card>

      <P type='1' weight='bold' variant='neutral'>
        {clientInfo.basic_data.company_name}
      </P>

      <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Personería:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.legal_entity}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Fecha de constitución:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.date_incorporation || '—'}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Condición frente al IVA:
          </P>
          <P type='2' variant='neutral'>
            {
              ivaStatuses.find(
                (item) => item.code === clientInfo.basic_data.iva_status
              )?.label
            }
          </P>
        </Card>
      </Card>

      <Card display='flex' gap='4px' width='35%' margin={{ top: '2' }}>
        <P type='2' variant='neutral' weight='bold'>
          Actividad económica:
        </P>
        <P type='2' variant='neutral'>
          {
            economicActivities.find(
              (item) => item.code === parseInt(clientInfo.basic_data.activity)
            )?.label || clientInfo.basic_data.activity || '—'
          }
        </P>
      </Card>

      <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Domicilio fiscal:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.company_address}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Localidad:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.city}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Provincia:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.state}
          </P>
        </Card>
      </Card>

      <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Teléfono:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.phone_number || (clientInfo as any).contact_phone}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Correo electrónico:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.basic_data.email || (clientInfo as any).contact_email}
          </P>
        </Card>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Referido:
          </P>
          <P type='2' variant='neutral'>
            {getReferrerNameLabel(clientInfo.referrer_name)}
          </P>
        </Card>
      </Card>

      <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Oficial:
          </P>
          <P type='2' variant='neutral'>
            {officers.find((item) => item.code === clientInfo.officer)?.label}
          </P>
        </Card>
        {isClientCategoryTypeEnabled && (
          <Card display='flex' gap='4px' width='30%'>
            <P type='2' variant='neutral' weight='bold'>
              Rubro:
            </P>
            <P type='2' variant='neutral'>
              {clientInfo.category_type?.name || '—'}
            </P>
          </Card>
        )}
        <Card display='flex' gap='4px' width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Comentarios:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.extra_comments}
          </P>
        </Card>
        {!isClientCategoryTypeEnabled && <Card display='flex' gap='4px' width='30%'></Card>}
      </Card>
      <Card display='flex' justifyContent='space-between' margin={{ top: '2' }}>
      <Card display='flex' gap='4px' margin={{ top: '2' }} width='30%'>
        <P type='2' variant='neutral' weight='bold'>
          Motivo:
        </P>
        <P type='2' variant='neutral'>
          {clientInfo.reason}
        </P>
      </Card>

      {isApplicationEffectiveDateEnabled && (
        <Card display='flex' gap='4px' margin={{ top: '2' }} width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Fecha de vigencia:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.effective_date ? new Date(clientInfo.effective_date).toLocaleDateString('es-AR') : '—'}
          </P>
        </Card>
      )}

      {isApplicationCreditTypeEnabled && (
        <Card display='flex' gap='4px' margin={{ top: '2' }} width='30%'>
          <P type='2' variant='neutral' weight='bold'>
            Clase de crédito:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.credit_type_name || '—'}
          </P>
        </Card>
      )}
      </Card>

      <Card display='flex' gap='4px' margin={{ top: '2' }}>
        <P type='2' variant='neutral' weight='bold'>
          Reseñas:
        </P>
        <P type='2' variant='neutral'>
          {clientInfo.review}
        </P>
      </Card>
    </Card>
  );
};

// Mantener compatibilidad con el uso anterior como función
const renderBasicData = (clientInfo: clientData): JSX.Element => {
  return <BasicData clientInfo={clientInfo} />;
};

export default renderBasicData;
export { BasicData };
